import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Icon } from 'leaflet';
import type { HomeComponentProps } from './helpers';
import LeafletFitBounds from '../../../components/utils/leafletFitBounds/LeafletFitBounds';
import { getProjectFromSiteId } from '../../../utils/projectSite';
// import { wktToPolygon } from '../../../utils/geom/highImpactGeometry';

const LeafIcon = Icon.extend({
  options: {
    iconUrl: '/images/map-pin.svg',
    iconSize: [26, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -24],
  },
});

const LeafIconOrange = Icon.extend({
  options: {
    iconUrl: '/images/map-pin-orange.svg',
    iconSize: [26, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -24],
  },
});

const LeafIconGreen = Icon.extend({
  options: {
    iconUrl: '/images/map-pin-green.svg',
    iconSize: [26, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -24],
  },
});

export const pin = new LeafIcon();
const pin_orange = new LeafIconOrange();
const pin_green = new LeafIconGreen();

export const site_id_to_pin: { [KEY in number]: any } = {
  9: pin_green,
  10: pin_orange,
  11: pin_green,
  12: pin,
  13: pin,
  14: pin_orange,
  //    15: pin,
};

export default function HomeMapComponent({
  project_sites,
  projects,
  sites,
  siteIdsToDashboardIds,
}: HomeComponentProps) {
  const navigate = useNavigate();

  const getProject = (site_id: number) =>
    getProjectFromSiteId(site_id, Object.values(project_sites.data), projects)
      ?.id ?? 0;

  const siteIdToGridId = Object.entries(siteIdsToDashboardIds.values).reduce(
    (acc, [siteId, gridIds]) => {
      return gridIds[0]
        ? {
            ...acc,
            [siteId]:
              gridIds.find((dashboard) => dashboard.type === 'user_dashboard')
                ?.id || 0,
          }
        : acc;
    },
    {} as Record<number, number>
  );

  // Prepare markers data
  const markers = Object.values(sites.data)
    .filter((site) => site.position?.coordinates) // Ensure coordinates exist
    .map((site) => ({
      id: site.id,
      name: site.name,
      position: site.position?.coordinates,
      icon: site_id_to_pin[site.id] || pin,
      dashboardId: siteIdToGridId[site.id] || 0,
    }));

  return (
    <MapContainer center={[46.651522, 2.390475]} zoom={6}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <LeafletFitBounds
        geometries={markers
          .filter((mrk) => mrk.position)
          .map((mrk) => ({
            type: 'POINT',
            coordinates: mrk.position as NonNullable<typeof mrk.position>,
          }))}
      />
      {markers
        .filter((marker) => marker.position)
        .map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position as NonNullable<typeof marker.position>}
            icon={marker.icon}
            eventHandlers={{
              mouseover: (event) => event.target.openPopup(),
              click: () =>
                navigate(
                  `/projects/${getProject(marker.id)}/sites/${
                    marker.id
                  }/tableau-de-bord/${marker.dashboardId}`
                ),
            }}
          >
            <Popup>{marker.name}</Popup>
          </Marker>
        ))}
    </MapContainer>
  );
}
